import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { startOfDay, isAfter, isBefore, differenceInCalendarDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { CollaboratorAllocation } from '@/features/Collaborators/Models';
import {
  ALLOCATION_TYPE_METADATA,
  ALLOCATION_BUCKET_METADATA,
  AllocationTypes,
} from '@/features/Collaborators/Models';
import { ALLOCATION_TYPE_ICON_MAP } from '@/features/Collaborators/Allocations/constants/allocationTypeIcons';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
import type { AllocationPreviewResult } from '@/features/Collaborators/Allocations/hooks/useCollaboratorAllocationPreviewStatus';

interface ProfileAllocationsCardProps {
  collaboratorId: string;
  allocations: CollaboratorAllocation[];
  allocationStatus: AllocationPreviewResult;
  isLoading: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  Allocated: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Idle: 'bg-rose-50 text-rose-700 ring-rose-200',
  IdleUpcoming: 'bg-amber-50 text-amber-700 ring-amber-200',
  NeedsReallocation: 'bg-amber-50 text-amber-700 ring-amber-200',
  Absence: 'bg-slate-50 text-slate-700 ring-slate-200',
};

const getSafeDate = (val: any): Date | null => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateShort = (val: any): string => {
  const d = getSafeDate(val);
  if (!d) return '-';
  return format(d, "dd MMM yyyy", { locale: ptBR });
};

const isActiveToday = (alloc: CollaboratorAllocation): boolean => {
  const today = startOfDay(new Date());
  const start = getSafeDate(alloc.startDate);
  const end = getSafeDate(alloc.endDate);
  if (!start) return false;
  return !isAfter(startOfDay(start), today) && (!end || !isBefore(startOfDay(end), today));
};

const isScheduled = (alloc: CollaboratorAllocation): boolean => {
  const today = startOfDay(new Date());
  const start = getSafeDate(alloc.startDate);
  if (!start) return false;
  return isAfter(startOfDay(start), today);
};

const isIgnoredType = (alloc: CollaboratorAllocation): boolean =>
  alloc.allocationType === AllocationTypes.Commercial ||
  alloc.allocationType === AllocationTypes.Provisional;

function formatBucket(alloc: CollaboratorAllocation): string {
  if (!alloc.bucket) return '';
  const meta = ALLOCATION_BUCKET_METADATA[alloc.bucketType];
  const unit = meta?.label?.toLowerCase() || '';
  return `${alloc.bucket}h/${unit}`;
}

function formatRemainingDays(alloc: CollaboratorAllocation): string | null {
  const end = getSafeDate(alloc.endDate);
  if (!end) return null;
  const days = differenceInCalendarDays(end, new Date());
  if (days < 0) return null;
  if (days === 0) return 'Termina hoje';
  if (days === 1) return 'Termina amanhã';
  return `${days} dias restantes`;
}

function AllocationItem({ alloc, showRemaining }: { alloc: CollaboratorAllocation; showRemaining?: boolean }) {
  const typeMeta = ALLOCATION_TYPE_METADATA[alloc.allocationType];
  const TypeIcon = ALLOCATION_TYPE_ICON_MAP[alloc.allocationType];
  const capacity = formatCapacityLabel(alloc);
  const bucket = formatBucket(alloc);
  const remaining = showRemaining ? formatRemainingDays(alloc) : null;
  const startDate = formatDateShort(alloc.startDate);
  const endDate = alloc.endDate ? formatDateShort(alloc.endDate) : 'Indefinido';

  const isAbsence = alloc.allocationType === AllocationTypes.Absence;
  const hasClient = !isAbsence && alloc.clientName;

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
          {TypeIcon && <TypeIcon className="h-4.5 w-4.5" />}
        </div>

        <div className="min-w-0 flex-1">
          {/* Header: Type + Badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">
              {typeMeta?.label || alloc.allocationType}
            </span>
            {bucket && (
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                {bucket}
              </span>
            )}
            {remaining && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                {remaining}
              </span>
            )}
          </div>

          {/* Client + Project */}
          {hasClient && (
            <div className="mt-1 flex items-center gap-1.5">
              {alloc.clientLogo && (
                <img
                  src={alloc.clientLogo}
                  alt=""
                  className="h-4 w-4 rounded-full object-cover"
                />
              )}
              <span className="text-sm text-slate-700">
                {alloc.clientName}
                {alloc.projectName && (
                  <span className="text-slate-400"> · {alloc.projectName}</span>
                )}
              </span>
            </div>
          )}

          {/* Capacity */}
          {capacity && (
            <p className="mt-1 text-xs text-slate-500">{capacity}</p>
          )}

          {/* Period */}
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
            <CalendarDaysIcon className="h-3.5 w-3.5" />
            <span>{startDate}</span>
            <ArrowRightIcon className="h-3 w-3" />
            <span>{endDate}</span>
          </div>
        </div>

        {/* Rate */}
        {alloc.rate != null && alloc.rate > 0 && (
          <div className="shrink-0 text-right">
            <p className="text-xs text-slate-400">Rate</p>
            <p className="text-sm font-semibold text-slate-700">
              {alloc.rate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProfileAllocationsCard({
  collaboratorId,
  allocations,
  allocationStatus,
  isLoading,
}: ProfileAllocationsCardProps) {
  const { activeAllocations, scheduledAllocations, totalActiveHoursWeekly } = useMemo(() => {
    const active = allocations
      .filter((a) => isActiveToday(a) && !isIgnoredType(a))
      .sort((a, b) => {
        const aStart = getSafeDate(a.startDate)?.getTime() ?? 0;
        const bStart = getSafeDate(b.startDate)?.getTime() ?? 0;
        return bStart - aStart;
      });

    const scheduled = allocations
      .filter((a) => isScheduled(a) && !isIgnoredType(a) && a.status !== 'Cancelled' && a.status !== 'Finished')
      .sort((a, b) => {
        const aStart = getSafeDate(a.startDate)?.getTime() ?? 0;
        const bStart = getSafeDate(b.startDate)?.getTime() ?? 0;
        return aStart - bStart;
      })
      .slice(0, 3);

    let weeklyHours = 0;
    for (const a of active) {
      if (!a.bucket) continue;
      switch (a.bucketType) {
        case 'Daily': weeklyHours += a.bucket * 5; break;
        case 'Weekly': weeklyHours += a.bucket; break;
        case 'Monthly': weeklyHours += a.bucket / 4; break;
        default: break;
      }
    }

    return {
      activeAllocations: active,
      scheduledAllocations: scheduled,
      totalActiveHoursWeekly: Math.round(weeklyHours),
    };
  }, [allocations]);

  const statusColor = STATUS_COLORS[allocationStatus.status] || 'bg-slate-50 text-slate-700 ring-slate-200';

  return (
    <section className="group relative flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ring-slate-100 transition duration-300 hover:shadow-md">
      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-900">Alocação</p>
            <p className="text-xs text-slate-500">Projetos e dedicação</p>
          </div>
        </div>
        <Link
          to={`/collaborators/${collaboratorId}/allocations`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 hover:text-indigo-900"
          aria-label="Ver histórico de alocações"
          onClick={(e) => e.stopPropagation()}
        >
          <DocumentPlusIcon className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      {/* Status Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusColor}`}
        >
          <span className="h-2 w-2 rounded-full bg-current" />
          {allocationStatus.label}
        </span>
        {totalActiveHoursWeekly > 0 && (
          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
            {totalActiveHoursWeekly}h/semana
          </span>
        )}
        {activeAllocations.length > 1 && (
          <span className="text-xs text-slate-500">
            {activeAllocations.length} alocações ativas
          </span>
        )}
      </div>

      {/* Active Allocations */}
      {activeAllocations.length > 0 && (
        <div className="mt-4 space-y-2">
          {activeAllocations.map((alloc) => (
            <AllocationItem key={alloc.id} alloc={alloc} showRemaining />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && activeAllocations.length === 0 && allocationStatus.status !== 'Absence' && (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center">
          <p className="text-sm text-slate-500">{allocationStatus.description}</p>
        </div>
      )}

      {/* Scheduled Allocations */}
      {scheduledAllocations.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Próximas alocações
          </p>
          <div className="space-y-2">
            {scheduledAllocations.map((alloc) => (
              <AllocationItem key={alloc.id} alloc={alloc} />
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="mt-4 flex justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
        </div>
      )}
    </section>
  );
}

export default ProfileAllocationsCard;
